import posthog from 'posthog-js';
import {
  isAnalyticsOptedOut,
  isInternalEmail,
  optInAnalyticsInBrowser,
  optOutAnalyticsInBrowser,
} from './opt-out';
import { resolveAnalyticsRoleContext } from './roles';
import type {
  AnalyticsApp,
  AnalyticsEventName,
  AnalyticsEventPayloads,
  AnalyticsProperties,
  AnalyticsRoleInput,
} from './types';

interface InitializePostHogOptions {
  app: AnalyticsApp;
  key?: string;
  apiHost?: string;
}

interface IdentifyPostHogUserOptions extends AnalyticsRoleInput {
  app: AnalyticsApp;
  id: number | string;
  email: string | null | undefined;
  firstName?: string | null;
  lastName?: string | null;
}

export function initializePrivacyFirstPostHog({
  app,
  key,
  apiHost = '/ingest',
}: InitializePostHogOptions): void {
  if (!key) return;
  const isOptedOut = typeof document !== 'undefined' && isAnalyticsOptedOut(document.cookie);

  posthog.init(key, {
    api_host: apiHost,
    defaults: '2026-05-30',
    opt_out_capturing_by_default: isOptedOut,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    capture_performance: {
      web_vitals: true,
    },
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
    },
    mask_all_text: false,
    mask_all_element_attributes: false,
    before_send: (event) => {
      if (!event) return null;

      const properties: AnalyticsProperties = {
        ...(event.properties as AnalyticsProperties | undefined),
        app,
      };

      return { ...event, properties };
    },
  });
}

export async function identifyPostHogUser({
  app,
  id,
  email,
  firstName,
  lastName,
  roles,
  teams,
  organisationId,
}: IdentifyPostHogUserOptions): Promise<void> {
  const normalizedEmail = email?.trim().toLowerCase();

  if (!normalizedEmail) return;

  const isInternalUser = isInternalEmail(normalizedEmail);
  if (isInternalUser) {
    optOutAnalyticsInBrowser();
    posthog.opt_out_capturing();
  } else {
    await optInAnalyticsInBrowser();
  }

  const normalizedFirstName = firstName?.trim() || undefined;
  const normalizedLastName = lastName?.trim() || undefined;
  const name = [normalizedFirstName, normalizedLastName].filter(Boolean).join(' ') || undefined;
  const roleContext = resolveAnalyticsRoleContext({
    roles,
    teams,
    organisationId,
  });

  posthog.identify(`user:${id}`, {
    app,
    email: normalizedEmail,
    first_name: normalizedFirstName,
    last_name: normalizedLastName,
    name,
    ...roleContext,
  });

  posthog.register({
    is_team_lead: roleContext.is_team_lead,
    people_role: roleContext.people_role,
    dashboard_group: roleContext.dashboard_group,
  });
}

export function resetPostHogUser(): void {
  posthog.reset();
}

export function getPostHogDistinctId(): string | undefined {
  return posthog.get_distinct_id() || undefined;
}

export function captureSafePostHogEvent<EventName extends AnalyticsEventName>(
  event: EventName,
  properties: AnalyticsEventPayloads[EventName]
): void {
  posthog.capture(event, properties);
}
